import * as React from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import DragIndicatorRounded from "@mui/icons-material/DragIndicatorRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";

export default function ReorderableList({ items, onChange }) {
  const dragIndex = React.useRef(null);
  const overIndex = React.useRef(null);
  const ghostRef = React.useRef(null);

  const commit = (next) => onChange?.(next);

  // ---- delete handler
  const handleDelete = (idx) => {
    const next = items.slice();
    next.splice(idx, 1);
    commit(next);
  };

  // ---- drag ghost (same as you had)
  const makeGhostFromNode = (node) => {
    const rect = node.getBoundingClientRect();
    const ghost = node.cloneNode(true);
    ghost.querySelectorAll(".MuiTouchRipple-root").forEach((n) => n.remove());
    const wrap = document.createElement("div");
    Object.assign(wrap.style, {
      position: "fixed",
      top: "-1000px",
      left: "-1000px",
      zIndex: "9999",
      pointerEvents: "none",
    });
    const cs = getComputedStyle(node);
    Object.assign(ghost.style, {
      boxSizing: "border-box",
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      margin: "0",
      background: cs.backgroundColor || "white",
      boxShadow: "0 4px 12px rgba(0,0,0,.2)",
    });
    wrap.appendChild(ghost);
    document.body.appendChild(wrap);
    return wrap;
  };

  const cleanupGhost = () => {
    const g = ghostRef.current;
    if (g && g.parentNode) g.parentNode.removeChild(g);
    ghostRef.current = null;
  };

  const handleDragStart = (idx, item) => (e) => {
    dragIndex.current = idx;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(idx));
    const row = e.currentTarget;
    const ghost = makeGhostFromNode(row);
    ghostRef.current = ghost;
    const rect = row.getBoundingClientRect();
    e.dataTransfer.setDragImage(
      ghost,
      Math.min(24, rect.width / 2),
      rect.height / 2
    );
  };

  const handleDragOver = (idx) => (e) => {
    e.preventDefault();
    overIndex.current = idx;
  };

  const handleDrop = (idx) => (e) => {
    e.preventDefault();
    const from = dragIndex.current;
    const to = idx;
    cleanupGhost();
    if (from == null || to == null || from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    dragIndex.current = null;
    overIndex.current = null;
    commit(next);
  };

  const handleDragEnd = () => {
    cleanupGhost();
    dragIndex.current = null;
    overIndex.current = null;
  };

  return (
    <Paper variant="outlined" sx={{ maxWidth: 420 }}>
      <List dense disablePadding>
        {items.map((item, idx) => (
          <ListItem
            key={item.approver_email + idx}
            disablePadding
            draggable
            onDragStart={handleDragStart(idx, item)}
            onDragOver={handleDragOver(idx)}
            onDrop={handleDrop(idx)}
            onDragEnd={handleDragEnd}
            // trailing delete button
            secondaryAction={
              <Tooltip title="Remove">
                <IconButton
                  edge="end"
                  size="small"
                  // prevent drag from starting when pressing delete
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(idx);
                  }}
                  aria-label={`Remove ${item.approver_name}`}
                >
                  <DeleteOutlineRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            }
            sx={(t) => ({
              cursor: "grab",
              "&:active": { cursor: "grabbing" },
              userSelect: "none",
              opacity: dragIndex.current === idx ? 0.6 : 1,
              bgcolor:
                overIndex.current === idx && dragIndex.current !== idx
                  ? t.palette.action.hover
                  : "transparent",
            })}
          >
            <ListItemButton sx={{ gap: 1 }}>
              <ListItemIcon sx={{ minWidth: 28, color: "text.secondary" }}>
                <DragIndicatorRounded fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={item.approver_name}
                secondary={item.approver_email}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Paper>
  );
}
